import { useState } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";

function OrganizationSettings() {
  const [orgData, setOrgData] = useState({
    companyName: "Property Management Corp",
    businessType: "property_management",
    address: "123 Main St",
    city: "New York",
    state: "NY",
    zipCode: "10001",
    phone: "(555) 123-4567",
    email: "admin@pmc.com",
    website: "www.pmc.com",
    taxId: "12-3456789",
    licenseNumber: "PM-2024-001",
    timezone: "America/New_York",
    defaultCurrency: "USD",
    fiscalYearStart: "January"
  });

  const [permissions, setPermissions] = useState({
    allowUserInvites: true,
    requireApprovalForNewUsers: true,
    enableTwoFactor: false,
    allowOptional2FA: true,
    allowPublicRegistration: false,
    autoAssignProperties: true,
    enableBulkOperations: true,
    allowDataExport: true,
    enableAPIAccess: false
  });

  const [branding, setBranding] = useState({
    logoUrl: "",
    primaryColor: "#1A73E8",
    companyDescription: "Leading property management solutions"
  });

  const handleOrgChange = (field, value) => {
    setOrgData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (permission) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  const handleBrandingChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = () => {
    console.log("Saving organization settings:", { orgData, permissions, branding });
  };

  return (
    <ArgonBox p={3}>
      <ArgonTypography variant="h4" fontWeight="bold" mb={3}>
        Organization Settings
      </ArgonTypography>

      {/* Company Information */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Company Information
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Company Name"
                label="Company Name"
                value={orgData.companyName}
                onChange={(e) => handleOrgChange('companyName', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={orgData.businessType}
                  onChange={(e) => handleOrgChange('businessType', e.target.value)}
                  label="Business Type"
                >
                  <MenuItem value="property_management">Property Management</MenuItem>
                  <MenuItem value="real_estate">Real Estate</MenuItem>
                  <MenuItem value="construction">Construction</MenuItem>
                  <MenuItem value="facilities">Facilities Management</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ArgonInput
                placeholder="Business Address"
                label="Address"
                value={orgData.address}
                onChange={(e) => handleOrgChange('address', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ArgonInput
                placeholder="City"
                label="City"
                value={orgData.city}
                onChange={(e) => handleOrgChange('city', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ArgonInput
                placeholder="State"
                label="State"
                value={orgData.state}
                onChange={(e) => handleOrgChange('state', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ArgonInput
                placeholder="ZIP Code"
                label="ZIP Code"
                value={orgData.zipCode}
                onChange={(e) => handleOrgChange('zipCode', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Phone Number"
                label="Phone"
                value={orgData.phone}
                onChange={(e) => handleOrgChange('phone', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Email"
                label="Email"
                value={orgData.email}
                onChange={(e) => handleOrgChange('email', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Website"
                label="Website"
                value={orgData.website}
                onChange={(e) => handleOrgChange('website', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Tax ID"
                label="Tax ID"
                value={orgData.taxId}
                onChange={(e) => handleOrgChange('taxId', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* System Preferences */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            System Preferences
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={orgData.timezone}
                  onChange={(e) => handleOrgChange('timezone', e.target.value)}
                  label="Timezone"
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Default Currency</InputLabel>
                <Select
                  value={orgData.defaultCurrency}
                  onChange={(e) => handleOrgChange('defaultCurrency', e.target.value)}
                  label="Default Currency"
                >
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                  <MenuItem value="CAD">CAD (C$)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Fiscal Year Start</InputLabel>
                <Select
                  value={orgData.fiscalYearStart}
                  onChange={(e) => handleOrgChange('fiscalYearStart', e.target.value)}
                  label="Fiscal Year Start"
                >
                  <MenuItem value="January">January</MenuItem>
                  <MenuItem value="April">April</MenuItem>
                  <MenuItem value="July">July</MenuItem>
                  <MenuItem value="October">October</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Security & Permissions */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Security & Permissions
          </ArgonTypography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.allowUserInvites}
                    onChange={() => handlePermissionChange('allowUserInvites')}
                  />
                }
                label="Allow User Invites"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.requireApprovalForNewUsers}
                    onChange={() => handlePermissionChange('requireApprovalForNewUsers')}
                  />
                }
                label="Require Approval for New Users"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.enableTwoFactor}
                    onChange={() => handlePermissionChange('enableTwoFactor')}
                  />
                }
                label="Require Two-Factor Authentication"
              />
              <ArgonTypography variant="caption" color="text" display="block" ml={3}>
                When enabled, all users must set up 2FA to access the system
              </ArgonTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.allowOptional2FA}
                    onChange={() => handlePermissionChange('allowOptional2FA')}
                    disabled={permissions.enableTwoFactor}
                  />
                }
                label="Allow Optional 2FA"
              />
              <ArgonTypography variant="caption" color="text" display="block" ml={3}>
                Let users choose to enable 2FA for their accounts
              </ArgonTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.allowPublicRegistration}
                    onChange={() => handlePermissionChange('allowPublicRegistration')}
                  />
                }
                label="Allow Public Registration"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.autoAssignProperties}
                    onChange={() => handlePermissionChange('autoAssignProperties')}
                  />
                }
                label="Auto-Assign Properties"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.enableBulkOperations}
                    onChange={() => handlePermissionChange('enableBulkOperations')}
                  />
                }
                label="Enable Bulk Operations"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.allowDataExport}
                    onChange={() => handlePermissionChange('allowDataExport')}
                  />
                }
                label="Allow Data Export"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissions.enableAPIAccess}
                    onChange={() => handlePermissionChange('enableAPIAccess')}
                  />
                }
                label="Enable API Access"
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Branding */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Branding
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="Logo URL"
                label="Company Logo URL"
                value={branding.logoUrl}
                onChange={(e) => handleBrandingChange('logoUrl', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonInput
                placeholder="#1A73E8"
                label="Primary Color"
                value={branding.primaryColor}
                onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <ArgonInput
                placeholder="Company Description"
                label="Company Description"
                value={branding.companyDescription}
                onChange={(e) => handleBrandingChange('companyDescription', e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Save Button */}
      <ArgonBox display="flex" justifyContent="flex-end">
        <ArgonButton
          variant="gradient"
          color="info"
          size="large"
          onClick={handleSaveSettings}
          startIcon={<Icon>save</Icon>}
        >
          Save Organization Settings
        </ArgonButton>
      </ArgonBox>
    </ArgonBox>
  );
}

export default OrganizationSettings;